import { useReactiveVar } from '@apollo/client';
import { confirmResetPassword, confirmSignIn, signIn, signOut } from '@aws-amplify/auth';
import { Button, Chip, Stack, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React, { useEffect, useState } from 'react';
import PasswordChecklist from 'react-password-checklist';
import { useNavigate } from 'react-router';
import { authVar } from 'state/vars';
import { parseCognitoUrlParams } from 'utils/ParseCognitoUrlParams/parseCognitoUrlParams';
import TKHSecurityLogo from 'v2/assets/vector/TKHSecurityLogo';
import AuthLayout from 'v2/components/AuthLayout/AuthLayout';
import useSnackbarContext from 'v2/components/Snackbar/useSnackbarContext';
import { HOME_PATH } from 'v2/paths';

type PageVariant = 'RESET' | 'SET';

export interface Props {
  pageVariant: PageVariant;
}

const knownKeys = ['code', 'email'];

const ResetOrSetPassword: React.FC<React.PropsWithChildren<Props>> = ({ pageVariant }) => {
  const theme = useTheme();
  const { user } = useReactiveVar(authVar);
  const { translation } = useTranslation();
  const params = parseCognitoUrlParams(window.location.href, knownKeys);
  const { code, email } = params;
  const { showSnackbar } = useSnackbarContext();
  const handleFetchError = (errorMessage: string) => showSnackbar(errorMessage, { severity: 'error' });
  const navigate = useNavigate();
  const [password, setPassword] = useState<string>('');
  const [rePassword, setRePassword] = useState<string>('');
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const [countdown, setCountdown] = useState<number>();

  useEffect(() => {
    if (pageVariant === 'SET' && user !== null) navigate(HOME_PATH);
  }, [user, pageVariant, navigate]);

  const handleResetAndLogin = async () => {
    if (isPasswordValid && code && email) {
      try {
        if (pageVariant === 'RESET') {
          await confirmResetPassword({
            username: email,
            confirmationCode: code,
            newPassword: password
          });
        } else {
          const resultSignIn = await signIn({ username: email, password: code });
          if (resultSignIn.nextStep) {
            const resultConfirmSignIn = await confirmSignIn({
              challengeResponse: password
            });
            if (resultConfirmSignIn) await signOut();
          }
        }
        setCountdown(15);
      } catch (err) {
        handleFetchError(translation.sign_out_error);
      }
    }
  };

  const isExtraSmallScreen = useMediaQuery('(min-width: 0px) and (max-width:627px)');
  const isSmallScreen = useMediaQuery('(min-width: 628px) and (max-width:643px)');
  const isMediumScreen = useMediaQuery('(min-width: 644px) and (max-width:663px)');

  const getPaperPadding = () => {
    if (isExtraSmallScreen || isSmallScreen) return '48px 24px';
    if (isMediumScreen) return '48px 40px';
    return '48px 80px';
  };

  const getPaperWidth = () => {
    if (isExtraSmallScreen) {
      return 'calc(100vw - 80px)';
    }
    if (isSmallScreen) {
      return '520px';
    }
    if (isMediumScreen) {
      return '552px';
    }
    return '632px';
  };

  useEffect(() => {
    let intervalId = null;
    if (countdown !== undefined && countdown > 0) {
      intervalId = setInterval(() => {
        setCountdown((prevCountdown) => prevCountdown! - 1);
      }, 1000);
    }

    if (countdown === 0) navigate(HOME_PATH);

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [countdown, navigate]);

  return (
    <AuthLayout paperPadding={getPaperPadding()} paperWidth={getPaperWidth()} windowId="reset-password-window">
      {countdown !== undefined ? (
        <Stack flexDirection="column" gap="40px">
          <Stack flexDirection="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h4">ATLAS</Typography>
            <TKHSecurityLogo sx={{ height: 40, width: 106 }} />
          </Stack>
          <Stack flexDirection="column">
            <Typography variant="h5">
              {pageVariant === 'RESET'
                ? translation.password_reset_successful
                : translation.account_created_successfully}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {
                translation.you_can_click_the_login_button_to_immediately_return_to_the_login_screen_or_wait_before_we_automatically_redirect_you_in_a_few_seconds
              }
            </Typography>
            <Typography marginTop="24px" variant="h5" alignSelf="center">
              {countdown}
            </Typography>
          </Stack>
          <Button
            id="login-button"
            onClick={() => navigate(HOME_PATH)}
            size="large"
            color="primary"
            variant="contained"
            style={{ color: theme.palette.primary.contrastText }}
          >
            {translation.login}
          </Button>
        </Stack>
      ) : (
        <Stack flexDirection="column" gap="40px">
          <Stack flexDirection="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h4">ATLAS</Typography>
            <TKHSecurityLogo sx={{ height: 40, width: 106 }} />
          </Stack>
          <Stack flexDirection="column" gap="24px">
            <div>
              <Chip size="medium" variant="filled" label={email} />
            </div>
            <form>
              <Stack flexDirection="column" gap="16px">
                <TextField
                  type="password"
                  label={translation.password}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  id="new-password"
                />
                <TextField
                  type="password"
                  label={translation.re_enter_password}
                  value={rePassword}
                  onChange={(event) => setRePassword(event.target.value)}
                  id="repeat-password"
                />
              </Stack>
            </form>
            <Typography variant="body2">{translation.password_requirements}</Typography>
            <Stack flexDirection="column" padding="0 8px" gap="2px">
              <PasswordChecklist
                rules={['minLength', 'specialChar', 'number', 'capital', 'lowercase', 'match']}
                minLength={8}
                value={password}
                valueAgain={rePassword}
                onChange={(isValid) => {
                  setIsPasswordValid(isValid);
                }}
                messages={{
                  minLength: translation.at_least_8_characters,
                  specialChar: translation.a_special_character,
                  number: translation.a_number,
                  capital: translation.a_capital_letter,
                  lowercase: translation.a_lowercase_letter,
                  match: translation.passwords_match
                }}
              />
            </Stack>
            <Button
              id="reset-password-submit-button"
              variant="contained"
              color="primary"
              onClick={handleResetAndLogin}
              disabled={!isPasswordValid}
            >
              {pageVariant === 'RESET' ? translation.complete_reset : translation.create_account}
            </Button>
          </Stack>
        </Stack>
      )}
    </AuthLayout>
  );
};

export default ResetOrSetPassword;
